import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { AxiosResponse } from 'axios';
import { useAppDispatch } from '../../../../hooks';
import catalogApiClient from '../../../../services/catalogapi';
import { alertError } from '../../../../slices/global/alertSlice';
import { ClassProps } from '../Class';

export interface LineProps {
  id: number;
  value: string;
}

interface Props {
  value: LineProps | null;
  classId: ClassProps | null;
  setValue: React.Dispatch<React.SetStateAction<LineProps | null>>;
  handleChange: (event: React.SyntheticEvent<Element, Event>, newValue: LineProps | null) => void;
}

export default function LineAutoComplete({ value, classId, setValue, handleChange }: Props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly LineProps[]>([]);
  const [inputValue, setInputValue] = React.useState<string>('');
  const loading = open && options.length === 0;
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const controller = new AbortController();
    let active = true;

    if (!loading || !classId) {
      return undefined;
    }

    (async () => {
      await catalogApiClient
        .get(`api/v1/fitment/parttype/GetLines?classId=${classId?.id}`, {
          signal: controller.signal,
        })
        .then((response: AxiosResponse<LineProps[]>) => {
          if (active) {
            setOptions([...response.data]);
          }
        })
        .catch(function (err) {
          if (err.message !== 'canceled')
            dispatch(alertError(`Failed to retrieve edi files. Reason: ${err}`));
        });
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [loading, dispatch, classId]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      disablePortal
      value={value}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      id="class-autocomplete"
      options={options}
      renderOption={(props, option) => <li {...props}>{option.value}</li>}
      getOptionLabel={(option) => option.value ?? ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={{ width: 500 }}
      renderInput={(params) => <TextField {...params} label="Line" />}
    />
  );
}
